import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Cookie, X, Settings, Shield, BarChart3, Sparkles, Megaphone, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const COOKIE_CONSENT_KEY = 'asap_cookie_consent';

// Default consent state
const defaultConsent = {
  essential: true, // Always true, cannot be changed
  analytics: false,
  functional: false,
  marketing: false,
  consentGiven: false,
  consentDate: null
};

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [consent, setConsent] = useState(defaultConsent);

  // Load consent from localStorage on mount
  useEffect(() => {
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (savedConsent) {
      const parsed = JSON.parse(savedConsent);
      setConsent(parsed);
      // Apply saved consent (load analytics if previously accepted)
      if (parsed.analytics) {
        loadAnalytics();
      }
    } else {
      // Show banner if no consent has been given
      setShowBanner(true);
    }
  }, []);

  // Save consent to localStorage
  const saveConsent = (newConsent) => {
    const consentWithDate = {
      ...newConsent,
      consentGiven: true,
      consentDate: new Date().toISOString()
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentWithDate));
    setConsent(consentWithDate);
  };

  // Load analytics scripts (only after consent)
  const loadAnalytics = () => {
    // Google Analytics 4 - placeholder for actual implementation
    // In production, replace with actual GA4 tracking code
    console.log('Analytics cookies enabled - GA4 would load here');
    
    // Example GA4 implementation (commented out, replace with actual tracking ID):
    // if (typeof window !== 'undefined' && !window.gtag) {
    //   const script = document.createElement('script');
    //   script.src = 'https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX';
    //   script.async = true;
    //   document.head.appendChild(script);
    //   
    //   window.dataLayer = window.dataLayer || [];
    //   function gtag(){dataLayer.push(arguments);}
    //   window.gtag = gtag;
    //   gtag('js', new Date());
    //   gtag('config', 'G-XXXXXXXXXX', { anonymize_ip: true });
    // }
  };

  // Accept all cookies
  const handleAcceptAll = () => {
    const newConsent = {
      essential: true,
      analytics: true,
      functional: true,
      marketing: false, // Marketing stays off by default per requirements
      consentGiven: true,
      consentDate: new Date().toISOString()
    };
    saveConsent(newConsent);
    loadAnalytics();
    setShowBanner(false);
    setShowModal(false);
  };

  // Reject non-essential cookies
  const handleRejectNonEssential = () => {
    const newConsent = {
      essential: true,
      analytics: false,
      functional: false,
      marketing: false,
      consentGiven: true,
      consentDate: new Date().toISOString()
    };
    saveConsent(newConsent);
    setShowBanner(false);
    setShowModal(false);
  };

  // Save custom preferences
  const handleSavePreferences = () => {
    saveConsent(consent);
    if (consent.analytics) {
      loadAnalytics();
    }
    setShowBanner(false);
    setShowModal(false);
  };

  // Toggle individual cookie category
  const toggleCategory = (category) => {
    if (category === 'essential') return; // Cannot toggle essential
    setConsent(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Cookie categories configuration
  const cookieCategories = [
    {
      id: 'essential',
      name: 'Essential Cookies',
      icon: Shield,
      description: 'These cookies are required for basic site functionality, form security, and system performance. They cannot be disabled.',
      required: true,
      color: 'emerald'
    },
    {
      id: 'analytics',
      name: 'Analytics Cookies',
      icon: BarChart3,
      description: 'These cookies help us understand how visitors interact with our website so we can improve performance and content. No personal financial information is collected.',
      required: false,
      color: 'blue'
    },
    {
      id: 'functional',
      name: 'Functional Cookies',
      icon: Sparkles,
      description: 'These cookies remember basic preferences such as language or region selection to improve user experience.',
      required: false,
      color: 'purple'
    },
    {
      id: 'marketing',
      name: 'Marketing Cookies',
      icon: Megaphone,
      description: 'These cookies may be used for advertising and remarketing purposes across platforms such as LinkedIn or Google. They are disabled by default.',
      required: false,
      color: 'amber',
      disabled: true // Marketing cookies disabled per requirements
    }
  ];

  // Don't render if consent already given and banner shouldn't show
  if (!showBanner && !showModal && consent.consentGiven) {
    return null;
  }

  return (
    <>
      {/* Cookie Banner */}
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
          >
            <div className="max-w-[1200px] mx-auto">
              <div className="bg-[#111D35] border border-white/10 rounded-2xl p-6 shadow-2xl shadow-black/50">
                <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                  {/* Icon and Text */}
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20 flex-shrink-0">
                      <Cookie className="h-6 w-6 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-2">Cookie Preferences</h3>
                      <p className="text-white/60 text-sm leading-relaxed">
                        We use cookies to ensure secure website functionality and to analyze site 
                        performance. You can accept or manage your cookie preferences at any time. 
                        See our{' '}
                        <Link 
                          to="/privacy" 
                          className="text-[#D4AF37] hover:text-[#F5D76E] underline"
                        >
                          Privacy Policy
                        </Link>{' '}
                        for more information.
                      </p>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
                    <Button
                      onClick={() => setShowModal(true)}
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/5 rounded-full px-5 h-10 text-sm"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Manage Preferences
                    </Button>
                    <Button
                      onClick={handleRejectNonEssential}
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/5 rounded-full px-5 h-10 text-sm"
                    >
                      Reject Non-Essential
                    </Button>
                    <Button
                      onClick={handleAcceptAll}
                      className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold rounded-full px-6 h-10 text-sm"
                    >
                      Accept All
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cookie Preferences Modal */}
      <AnimatePresence>
        {showModal && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[10000]"
              onClick={() => setShowModal(false)}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[10001] w-full max-w-lg max-h-[90vh] overflow-hidden"
            >
              <div className="bg-[#111D35] border border-white/10 rounded-2xl shadow-2xl shadow-black/50">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                      <Cookie className="h-5 w-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white">Cookie Preferences</h2>
                      <p className="text-white/40 text-xs">Manage your cookie settings</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowModal(false)}
                    className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Cookie Categories */}
                <div className="p-6 max-h-[50vh] overflow-y-auto">
                  <div className="space-y-4">
                    {cookieCategories.map((category) => {
                      const Icon = category.icon;
                      const isEnabled = consent[category.id];
                      const isDisabled = category.disabled;

                      return (
                        <div 
                          key={category.id}
                          className={`p-4 rounded-xl border transition-all ${
                            isEnabled 
                              ? 'bg-white/5 border-white/10' 
                              : 'bg-white/[0.02] border-white/5'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3 flex-1">
                              <div 
                                className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0`}
                                style={{
                                  background: category.color === 'emerald' ? 'rgba(16, 185, 129, 0.1)' :
                                             category.color === 'blue' ? 'rgba(59, 130, 246, 0.1)' :
                                             category.color === 'purple' ? 'rgba(168, 85, 247, 0.1)' :
                                             'rgba(245, 158, 11, 0.1)'
                                }}
                              >
                                <Icon 
                                  className="h-5 w-5"
                                  style={{
                                    color: category.color === 'emerald' ? '#34d399' :
                                           category.color === 'blue' ? '#60a5fa' :
                                           category.color === 'purple' ? '#c084fc' :
                                           '#fbbf24'
                                  }}
                                />
                              </div>
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="text-white font-medium text-sm">{category.name}</h4>
                                  {category.required && (
                                    <span className="px-2 py-0.5 rounded text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                                      Always Active
                                    </span>
                                  )}
                                  {isDisabled && (
                                    <span className="px-2 py-0.5 rounded text-xs bg-white/10 text-white/40 border border-white/10">
                                      Disabled
                                    </span>
                                  )}
                                </div>
                                <p className="text-white/50 text-xs leading-relaxed">
                                  {category.description}
                                </p>
                              </div>
                            </div>

                            {/* Toggle */}
                            {!category.required && !isDisabled && (
                              <button
                                onClick={() => toggleCategory(category.id)}
                                className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                                  isEnabled ? 'bg-[#D4AF37]' : 'bg-white/20'
                                }`}
                              >
                                <span
                                  className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                                    isEnabled ? 'left-7' : 'left-1'
                                  }`}
                                />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Privacy Policy Link */}
                  <div className="mt-6 pt-4 border-t border-white/5">
                    <p className="text-white/40 text-xs text-center">
                      For more information about how we use cookies and your data, please read our{' '}
                      <Link 
                        to="/privacy" 
                        className="text-[#D4AF37] hover:text-[#F5D76E]"
                        onClick={() => setShowModal(false)}
                      >
                        Privacy Policy
                      </Link>{' '}
                      and{' '}
                      <Link 
                        to="/disclosures" 
                        className="text-[#D4AF37] hover:text-[#F5D76E]"
                        onClick={() => setShowModal(false)}
                      >
                        Disclosures
                      </Link>.
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex flex-col sm:flex-row gap-3 p-6 border-t border-white/10 bg-white/[0.02]">
                  <Button
                    onClick={handleRejectNonEssential}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/5 rounded-full flex-1 h-10 text-sm"
                  >
                    Reject Non-Essential
                  </Button>
                  <Button
                    onClick={handleSavePreferences}
                    className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold rounded-full flex-1 h-10 text-sm"
                  >
                    Save Preferences
                  </Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

// Export utility function to check consent status
export const getCookieConsent = () => {
  if (typeof window === 'undefined') return defaultConsent;
  const saved = localStorage.getItem(COOKIE_CONSENT_KEY);
  return saved ? JSON.parse(saved) : defaultConsent;
};

// Export utility function to open cookie settings
export const openCookieSettings = () => {
  // Dispatch custom event to open modal
  window.dispatchEvent(new CustomEvent('openCookieSettings'));
};

export default CookieConsent;
